import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { User } from 'src/app/domain/user/types/User.model';
import { AvatarComponent } from 'src/app/ui-kit/avatar/avatar.component';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';
import { StatusDotComponent } from 'src/app/ui-kit/status-dot/status-dot.component';

/**
 * Dense list-row for a Blume {@link User}. Avatar, name, role subtitle, and a status
 * dot driven by `deactivated` / `inviteAccepted`.
 *
 * @security FERPA — email, first/last name are PII. Never log.
 *
 * @feature shared
 */
@Component({
  selector: 'app-user-list-item',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AvatarComponent, IconComponent, StatusDotComponent],
  host: { class: 'block' },
  template: `
    <button
      type="button"
      class="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:shadow-[var(--theme-focus-ring)]"
      [disabled]="!clickable()"
      [class.cursor-default]="!clickable()"
      (click)="onClick()"
      [attr.aria-label]="'Open ' + fullName() + ' profile'"
    >
      <app-avatar [name]="fullName()" size="sm" />

      <div class="min-w-0 flex-1">
        <p class="truncate text-sm font-semibold text-body">{{ fullName() }}</p>
        @if (subtitle()) {
          <p class="truncate text-xs text-muted">{{ subtitle() }}</p>
        }
      </div>

      <app-status-dot [status]="statusDot()" [ariaLabel]="statusLabel()" />

      @if (clickable()) {
        <app-icon name="chevron-forward" size="xs" color="medium" />
      }
    </button>
  `,
})
export class UserListItemComponent {
  /** The user to render. @security FERPA. */
  readonly user = input.required<User>();

  /** Whether the row is interactive. @default true */
  readonly clickable = input<boolean>(true);

  /** Fires when the row is clicked. */
  readonly rowClick = output<User>();

  readonly fullName = computed(() => {
    const u = this.user();
    const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
    return name || u.email || 'Unknown user';
  });

  readonly subtitle = computed(() => {
    const u = this.user();
    const parts: string[] = [];
    if (u.jobTitle) parts.push(u.jobTitle);
    if (u.email && !u.jobTitle) parts.push(u.email);
    return parts.join(' · ');
  });

  readonly statusDot = computed(() => {
    const u = this.user();
    if (u.deactivated) return 'off-track' as const;
    if (u.inviteAccepted === false) return 'warning' as const;
    return 'on-track' as const;
  });

  readonly statusLabel = computed(() => {
    const u = this.user();
    if (u.deactivated) return 'Deactivated';
    if (u.inviteAccepted === false) return 'Invite pending';
    return 'Active';
  });

  onClick(): void {
    if (this.clickable()) {
      this.rowClick.emit(this.user());
    }
  }
}
